/* useSeoMeta — small hook that fetches SEO metadata from
 * `/api/seo?route=<path>` and applies it imperatively to the document
 * head. Keeps the landing pages dependency-free (no react-helmet).
 *
 * The admin-configured fields handled:
 *   - title, description, keywords (csv string), og_title, og_description,
 *     og_image, canonical, robots
 *
 * On unmount / route change the previous values are restored so the
 * The Loom app doesn't inherit a marketing page's metadata.
 */
import { useEffect } from 'react';

// SEO audit fix — og:locale BCP-47 map (OG spec uses underscore notation).
const OG_LOCALE_MAP = {
  en: 'en_US', fr: 'fr_FR', de: 'de_DE', es: 'es_ES', pt: 'pt_BR', zh: 'zh_CN',
};
const ALL_OG_LOCALES = Object.values(OG_LOCALE_MAP);

/** Detect the active locale from the current URL path prefix. */
function _detectLang() {
  const m = window.location.pathname.match(/^\/(fr|de|es|pt|zh)(\/|$)/);
  return m ? m[1] : 'en';
}

function upsertMeta(attr, key, value) {
  if (value == null) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const prev = el ? el.getAttribute('content') : null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(value || ''));
  return { el, prev };
}

function upsertLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const prev = el ? el.getAttribute('href') : null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', String(href));
  return { el, prev };
}

export function useSeoMeta(route, fallback = {}) {
  useEffect(() => {
    const restorers = [];
    const prevTitle = document.title;

    // 2026-10 · SEO audit fix — this hook used to force document.title
    // back to the generic "Crown & Rose LLC" brand name on every public
    // page, undoing the unique per-route title crawlers see from the
    // server-side prerender middleware the moment React hydrates. Every
    // caller of this hook is a public marketing/bookstore/book/author
    // page (never the authenticated /app or /admin surfaces, which
    // never call it) — so there's no multi-tab-confusion downside to
    // letting the real title show here too.
    const DEFAULT_TITLE = 'Crown & Rose — Multi-Model AI Writing Studio & Book Store';
    const apply = (data) => {
      document.title = data.title || DEFAULT_TITLE;
      const lang = _detectLang();
      const ogLocale = OG_LOCALE_MAP[lang] || 'en_US';
      const slots = [
        ['name',     'description',          data.description],
        ['name',     'keywords',             data.keywords],
        ['name',     'robots',               data.robots],
        ['property', 'og:title',             data.og_title || data.title],
        ['property', 'og:description',       data.og_description || data.description],
        ['property', 'og:image',             data.og_image],
        ['property', 'og:url',               data.canonical],
        ['property', 'og:type',              data.og_type || 'website'],
        ['property', 'og:locale',            ogLocale],
        ['name',     'twitter:card',         data.og_image ? 'summary_large_image' : 'summary'],
        ['name',     'twitter:title',        data.og_title || data.title],
        ['name',     'twitter:description',  data.og_description || data.description],
        ['name',     'twitter:image',        data.og_image],
      ];
      for (const [attr, key, value] of slots) {
        if (value) {
          const r = upsertMeta(attr, key, value);
          if (r) restorers.push(() => { if (r.prev == null) r.el.remove(); else r.el.setAttribute('content', r.prev); });
        }
      }
      // og:locale:alternate — one tag per alternate locale (multi-valued OG).
      // Managed as a keyed set so they don't accumulate on re-renders.
      const alts = ALL_OG_LOCALES.filter((l) => l !== ogLocale);
      // Remove any stale og:locale:alternate tags from a prior locale
      document.head.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
      alts.forEach((altLocale) => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'og:locale:alternate');
        el.setAttribute('content', altLocale);
        document.head.appendChild(el);
        restorers.push(() => { try { el.remove(); } catch { /* noop */ } });
      });
      const canon = upsertLink('canonical', data.canonical);
      if (canon) restorers.push(() => { if (canon.prev == null) canon.el.remove(); else canon.el.setAttribute('href', canon.prev); });
    };

    // Optimistic fallback on first render so search-engine fetchers that
    // scrape immediately don't see a blank title.
    apply(fallback);

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/seo?route=${encodeURIComponent(route)}`;
    fetch(url).then((r) => r.ok ? r.json() : null).then((data) => {
      if (data) apply(data);
    }).catch((err) => {
      // Non-fatal — fallback metadata has already been applied. Trace so
      // backend SEO outages aren't completely silent.
      console.debug('[useSeoMeta] /api/seo fetch failed:', err);
    });

    return () => {
      document.title = prevTitle;
      for (const fn of restorers) {
        try { fn(); } catch (err) {
          // A restorer threw — meta tag was probably removed by another
          // component. Safe to ignore but log so flaky cleanups are visible.
          console.debug('[useSeoMeta] restorer threw:', err);
        }
      }
    };
    // `fallback` intentionally omitted — only re-applies if the route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);
}
